import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';

export interface UploadResult {
  url: string;
  path: string;  // Firebase Storage path for refreshing expired URLs
  size: number;
  name: string;
}

export const uploadService = {
  // Upload SVG file to Firebase Storage (existing function)
  async uploadSVG(file: File): Promise<UploadResult> {
    try {
      // Validate file type
      if (file.type !== 'image/svg+xml') {
        throw new Error('Only SVG files are allowed');
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('File size must be less than 5MB');
      }

      // Create unique filename
      const timestamp = Date.now();
      const filename = `logos/${timestamp}_${file.name}`;
      const storageRef = ref(storage, filename);

      // Upload file
      console.log('Uploading file:', filename);
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      console.log('Upload successful:', downloadURL);

      return {
        url: downloadURL,
        path: filename,
        size: file.size,
        name: file.name
      };
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  },

  // Upload profile image to Firebase Storage (existing function)
  async uploadProfileImage(file: File): Promise<UploadResult> {
    try {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Only JPG, PNG, and WebP images are allowed');
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('Image size must be less than 5MB');
      }

      // Create unique filename with proper extension
      const timestamp = Date.now();
      const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const filename = `profiles/${timestamp}_profile.${extension}`;
      const storageRef = ref(storage, filename);

      // Upload file
      console.log('Uploading profile image:', filename);
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      console.log('Profile image upload successful:', downloadURL);

      return {
        url: downloadURL,
        path: filename,
        size: file.size,
        name: file.name
      };
    } catch (error) {
      console.error('Profile image upload error:', error);
      throw error;
    }
  },

  // Upload suggestion attachment (NEW FUNCTION)
  async uploadSuggestionFile(file: File): Promise<UploadResult> {
    try {
      // Validate file types (images, PDFs, documents)
      const allowedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif',
        'application/pdf',
        'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        throw new Error('File type not allowed. Please upload images, PDFs, or Office documents only.');
      }

      // Validate file size (max 10MB for suggestions)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }

      // Create unique filename with proper extension
      const timestamp = Date.now();
      const extension = file.name.split('.').pop()?.toLowerCase() || 'file';
      const cleanName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filename = `suggestions/${timestamp}_${cleanName}`;
      const storageRef = ref(storage, filename);

      // Upload file
      console.log('Uploading suggestion file:', filename);
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      console.log('Suggestion file upload successful:', downloadURL);

      return {
        url: downloadURL,
        path: filename,
        size: file.size,
        name: file.name
      };
    } catch (error) {
      console.error('Suggestion file upload error:', error);
      throw error;
    }
  },

  // Validate SVG file before upload (existing function)
  validateSVGFile(file: File): { valid: boolean; error?: string } {
    if (!file) {
      return { valid: false, error: 'No file selected' };
    }

    if (file.type !== 'image/svg+xml') {
      return { valid: false, error: 'Only SVG files are allowed' };
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { valid: false, error: 'File size must be less than 5MB' };
    }

    if (file.size < 100) { // Very small files are suspicious
      return { valid: false, error: 'File appears to be corrupted or empty' };
    }

    return { valid: true };
  },

  // Validate profile image file before upload (existing function)
  validateProfileImage(file: File): { valid: boolean; error?: string } {
    if (!file) {
      return { valid: false, error: 'No file selected' };
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Only JPG, PNG, and WebP images are allowed' };
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { valid: false, error: 'Image size must be less than 5MB' };
    }

    if (file.size < 1000) { // Very small files are suspicious
      return { valid: false, error: 'Image appears to be corrupted or empty' };
    }

    return { valid: true };
  },

  // Validate suggestion file before upload (NEW FUNCTION)
  validateSuggestionFile(file: File): { valid: boolean; error?: string } {
    if (!file) {
      return { valid: false, error: 'No file selected' };
    }

    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif',
      'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'File type not allowed. Please upload images, PDFs, or Office documents only.' };
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return { valid: false, error: 'File size must be less than 10MB' };
    }

    if (file.size < 10) { // Very small files are suspicious
      return { valid: false, error: 'File appears to be corrupted or empty' };
    }

    return { valid: true };
  }
};